// Copyright 2026 Bitech.id. Licensed under the MIT License.

interface TimestampedEvent {
  event: unknown;
  timestampMs: number;
}

/**
 * Holds the last `bufferSeconds` of rrweb events in memory without
 * uploading them — used in 'buffered' mode so a session that turns out to
 * hit an error can be promoted and its recent history uploaded, instead of
 * only capturing what happens *after* the error.
 */
export class RingBuffer {
  private events: TimestampedEvent[] = [];

  constructor(private readonly bufferSeconds: number) {}

  add(event: unknown, timestampMs: number): void {
    this.events.push({ event, timestampMs });
    this.evictOlderThan(Date.now() - this.bufferSeconds * 1000);
  }

  private evictOlderThan(cutoffMs: number): void {
    let dropCount = 0;
    while (dropCount < this.events.length && this.events[dropCount]!.timestampMs < cutoffMs) {
      dropCount++;
    }
    if (dropCount > 0) this.events.splice(0, dropCount);
  }

  /** Drains and returns all buffered events, oldest first, clearing the buffer. */
  drain(): unknown[] {
    const drained = this.events.map((e) => e.event);
    this.events = [];
    return drained;
  }

  get size(): number {
    return this.events.length;
  }
}
